import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "productItem", "productContent", "mobileMenu", "productMenu", "productSubMenu",
    "dropdown", "productsLink", "topBar", "productMenuItem", "dienstleistungenLink",
    "dienstleistungenDropdown", "dienstleistungenMenu", "dienstleistungenMenuItem",
    "serviceItem", "serviceContent", "serviceSubMenu", "productsContainer",
    "dienstleistungenContainer", "desktopMenu", "phoneNumber", "beratungButton", "navbarContainer", "menuButton", "menuIcon", "closeIcon"
  ]

  connect() {
    this.initializeNavbar();
    this.lastScrollY = window.scrollY;
    this.isNavbarVisible = true;
    this.scrollThreshold = 50;
    this.debounceTimer = null;
    this.isDropdownOpen = false;
    this.setMobileMenuPosition();
    window.addEventListener('resize', this.setMobileMenuPosition.bind(this));
  }

  initializeNavbar() {
    this.handleResponsiveness();
    this.setupScrollBehavior();
    this.setupDropdownBehavior();
    this.setupResizeListener();
    this.initializeState();
    this.setupHoverBehavior();
  }


  initializeState() {
    this.lastScrollY = 0;
    this.currentDropdown = null;
    this.isOverLink = { products: false, dienstleistungen: false };
    this.isOverDropdown = { products: false, dienstleistungen: false };
    this.hideTimeouts = { products: null, dienstleistungen: null };
    this.element.style.zIndex = '9999';
    this.isMobileMenuOpen = false;
  }

  setupDropdownBehavior() {
    this.setupDropdownListeners('products', this.productsContainerTarget, this.dropdownTarget);
    this.setupDropdownListeners('dienstleistungen', this.dienstleistungenContainerTarget, this.dienstleistungenDropdownTarget);
  }

  setupDropdownListeners(type, containerTarget, dropdownTarget) {
    containerTarget.addEventListener('mouseenter', () => this.handleMouseEnter(type, 'link'));
    containerTarget.addEventListener('mouseleave', () => this.handleMouseLeave(type, 'link'));
    dropdownTarget.addEventListener('mouseenter', () => this.handleMouseEnter(type, 'dropdown'));
    dropdownTarget.addEventListener('mouseleave', () => this.handleMouseLeave(type, 'dropdown'));
  }

  handleResponsiveness() {
    const isDesktop = window.innerWidth >= 1024;
    this.toggleDesktopElements(isDesktop);
    if (isDesktop) {
      this.showProduct({ currentTarget: this.productItemTargets[0] });
      this.showService({ currentTarget: this.serviceItemTargets[0] });
      // Reset mobile menu and icon when switching to desktop
      if (this.isMobileMenuOpen) {
        this.toggleMobileMenu();
      }
    }
  }

  toggleDesktopElements(show) {
    [this.desktopMenuTarget, this.phoneNumberTarget, this.beratungButtonTarget].forEach(el => {
      el.classList.toggle('hidden', !show);
    });
  }

  setupResizeListener() {
    window.addEventListener('resize', () => this.handleResponsiveness());
  }

  setupScrollBehavior() {
    window.addEventListener('scroll', this.debounce(this.handleScroll.bind(this), 10), { passive: true });
  }

  setupHoverBehavior() {
    this.navbarContainerTarget.addEventListener('mouseenter', this.showOnHover.bind(this));
  }

  debounce(func, wait) {
    return () => {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        func();
      }, wait);
    };
  }

  handleScroll() {
    if (this.isMobileMenuOpen || this.isDropdownOpen) return;

    const currentScrollY = window.scrollY;
    const scrollDifference = currentScrollY - this.lastScrollY;

    if (Math.abs(scrollDifference) < this.scrollThreshold) return;

    if (scrollDifference > 0 && this.isNavbarVisible) {
      this.hideDesktopMenu();
    } else if (scrollDifference < 0 && !this.isNavbarVisible) {
      this.showDesktopMenu();
    }

    this.lastScrollY = currentScrollY;
  }

  toggleMenu(menuTarget, itemTarget = null) {
    menuTarget.classList.toggle('hidden');
    if (itemTarget) itemTarget.classList.toggle('bg-gray-100');
    this.toggleArrow(event.currentTarget);
  }

  toggleArrow(element) {
    const arrow = element.querySelector('svg:last-child');
    if (arrow) arrow.classList.toggle('rotate-180');
  }

  toggleProductMenu() {
    this.toggleMenu(this.productMenuTarget, this.productMenuItemTarget);
  }

  toggleDienstleistungenMenu() {
    this.toggleMenu(this.dienstleistungenMenuTarget, event.currentTarget);
  }

  toggleSubMenu(event, targetName) {
    const type = event.currentTarget.dataset[targetName];
    const subMenu = this[`${targetName}SubMenuTargets`].find(target => target.dataset[targetName] === type);
    this.toggleMenu(subMenu);
    if (targetName === 'product') this.productMenuItemTarget.classList.add('bg-gray-100');
  }


  toggleMobileMenu() {
    const mobileMenu = this.mobileMenuTarget;
    const headerHeight = parseInt(mobileMenu.dataset.headerHeight, 10);

    if (mobileMenu.classList.contains('hidden')) {
      mobileMenu.classList.remove('hidden');
      mobileMenu.style.transform = `translateX(100%)`;
      mobileMenu.style.transition = 'transform 0.3s ease-out';
      requestAnimationFrame(() => {
        mobileMenu.style.transform = 'translateX(0)';
      });
      this.isMobileMenuOpen = true;
      this.toggleMenuIcon(true);
    } else {
      mobileMenu.style.transform = 'translateX(100%)';
      mobileMenu.addEventListener('transitionend', () => {
        mobileMenu.classList.add('hidden');
      }, { once: true });
      this.isMobileMenuOpen = false;
      this.toggleMenuIcon(false);
    }
  }

  toggleMenuIcon(isOpen) {
    this.menuIconTarget.classList.toggle('hidden', isOpen);
    this.closeIconTarget.classList.toggle('hidden', !isOpen);
  }

  setMobileMenuPosition() {
    const header = this.navbarContainerTarget;
    const mobileMenu = this.mobileMenuTarget;
    const headerHeight = header.offsetHeight;
    mobileMenu.style.top = `${headerHeight}px`;
    mobileMenu.dataset.headerHeight = headerHeight;
  }

  toggleProductSubMenu(event) {
    this.toggleSubMenu(event, 'product');
  }

  toggleServiceSubMenu(event) {
    this.toggleSubMenu(event, 'service');
  }

  showDropdown(type) {
    if (this.currentDropdown && this.currentDropdown !== type) {
      this.hideDropdown(this.currentDropdown);
    }
    this.currentDropdown = type;
    const target = type === 'products' ? this.dropdownTarget : this.dienstleistungenDropdownTarget;
    const link = type === 'products' ? this.productsLinkTarget : this.dienstleistungenLinkTarget;
    target.classList.remove('hidden');
    link.classList.add('text-ph24_orange');
    target.style.zIndex = '10000';
    this.isDropdownOpen = true;
    this.showDesktopMenu();
  }

  hideDropdown(type) {
    const target = type === 'products' ? this.dropdownTarget : this.dienstleistungenDropdownTarget;
    const link = type === 'products' ? this.productsLinkTarget : this.dienstleistungenLinkTarget;
    target.classList.add('hidden');
    link.classList.remove('text-ph24_orange');
    target.style.zIndex = '';
    if (this.currentDropdown === type) {
      this.currentDropdown = null;
    }
    this.isDropdownOpen = false;
  }

  handleMouseEnter(type, area) {
    clearTimeout(this.hideTimeouts[type]);
    this.isOverLink[type] = area === 'link';
    this.isOverDropdown[type] = area === 'dropdown';
    this.showDropdown(type);
  }

  handleMouseLeave(type, area) {
    this.isOverLink[type] = false;
    this.isOverDropdown[type] = false;
    this.hideTimeouts[type] = setTimeout(() => {
      if (!this.isOverLink[type] && !this.isOverDropdown[type]) {
        this.hideDropdown(type);
      }
    }, 100);
  }

  showContent(event, contentType) {
    const type = event.currentTarget.dataset[contentType];
    this[`${contentType}ItemTargets`].forEach(item => {
      const isActive = item.dataset[contentType] === type;
      item.classList.toggle('bg-gray-100', isActive);
      item.querySelector('svg').classList.toggle('text-ph24_blue', isActive);
      item.querySelector('svg').classList.toggle('text-gray-400', !isActive);
      item.querySelector('span').classList.toggle('font-semibold', isActive);
    });
    this[`${contentType}ContentTargets`].forEach(content => {
      content.classList.toggle('hidden', content.dataset[contentType] !== type);
    });
  }

  showProduct(event) {
    this.showContent(event, 'product');
  }

  showService(event) {
    this.showContent(event, 'service');
  }

  showOnHover() {
    if (!this.isNavbarVisible) {
      this.showDesktopMenu();
    }
  }

  hideDesktopMenu() {
    if (!this.isNavbarVisible) return;
    this.desktopMenuTarget.classList.add('desktop-menu-hidden');
    this.isNavbarVisible = false;
  }

  showDesktopMenu() {
    if (this.isNavbarVisible) return;
    this.desktopMenuTarget.classList.remove('desktop-menu-hidden');
    this.isNavbarVisible = true;
  }

  // Add these methods for the dropdown functionality
  cancelHideDropdown() {
    clearTimeout(this.hideTimeouts.products);
  }

  cancelHideDienstleistungenDropdown() {
    clearTimeout(this.hideTimeouts.dienstleistungen);
  }
}
